import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Camera, ChevronLeft, ChevronRight, Globe, Image, Monitor, EyeOff,
  ArrowLeft, Users, Trash2, Loader2, Search, LayoutGrid, Sparkles,
} from 'lucide-react';
import { useReportFilters } from './useReportFilters';
import { DateNavInput } from './DateNavInput';
import { toast } from 'sonner';
import { formatUtcTime } from '@/utils/dateUtils';

export default function ScreenshotsReportPage() {
  const navigate = useNavigate();
  const { timezone, todayDate } = useReportFilters();

  const [selectedDate, setSelectedDate] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (todayDate) setSelectedDate(todayDate);
  }, [todayDate]);

  const [summary, setSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [detailStaff, setDetailStaff] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedScreenshots, setSelectedScreenshots] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [viewingScreenshot, setViewingScreenshot] = useState(null);
  const [viewingImageUrl, setViewingImageUrl] = useState(null);
  const [viewingImageLoading, setViewingImageLoading] = useState(false);

  useEffect(() => {
    if (selectedDate) fetchSummary();
  }, [selectedDate]);

  const fetchSummary = async () => {
    setLoadingSummary(true);
    setSummary(null);
    setDetailStaff(null);
    try {
      const res = await axios.get(`${API}/admin/reports/screenshots-summary`, {
        params: { date: selectedDate },
        withCredentials: true,
      });
      setSummary(res.data);
    } catch {
      toast.error('Failed to load screenshots summary');
    } finally {
      setLoadingSummary(false);
    }
  };

  const openDetail = (staff) => {
    setDetailStaff(staff);
    setPage(1);
    setSelectedScreenshots([]);
    setQuery('');
  };

  useEffect(() => {
    if (detailStaff) fetchDetail();
  }, [detailStaff, page]);

  const fetchDetail = async () => {
    setLoadingDetail(true);
    setDetailData(null);
    try {
      const res = await axios.get(
        `${API}/admin/activities/screenshots/${detailStaff.staff_id}`,
        { params: { date: selectedDate, page, limit: 30 }, withCredentials: true }
      );
      setDetailData(res.data);
    } catch {
      toast.error('Failed to load screenshots');
    } finally {
      setLoadingDetail(false);
    }
  };

  const thumbnailUrl = (ss) => (ss.image_path ? `${API}${ss.image_path}` : null);

  const screenshots = detailData?.screenshots || [];
  const allSelected = screenshots.length > 0 && selectedScreenshots.length === screenshots.length;

  const handleSelectAll = (checked) => {
    setSelectedScreenshots(checked ? screenshots.map((s) => s.screenshot_id) : []);
  };

  const handleSelectScreenshot = (id, checked) => {
    setSelectedScreenshots((prev) =>
      checked ? [...prev, id] : prev.filter((i) => i !== id)
    );
  };

  const handleDeleteSelected = async () => {
    setDeleting(true);
    try {
      await axios.post(
        `${API}/admin/activities/screenshots/bulk-delete`,
        { screenshot_ids: selectedScreenshots },
        { withCredentials: true }
      );
      toast.success(`Deleted ${selectedScreenshots.length} screenshot(s)`);
      setSelectedScreenshots([]);
      setDeleteDialogOpen(false);
      await fetchDetail();
    } catch {
      toast.error('Failed to delete screenshots');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteSingle = async (screenshotId, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/admin/activities/screenshots/${screenshotId}`, {
        withCredentials: true,
      });
      toast.success('Screenshot deleted');
      setSelectedScreenshots((prev) => prev.filter((i) => i !== screenshotId));
      await fetchDetail();
    } catch {
      toast.error('Failed to delete screenshot');
    }
  };

  const openViewer = async (ss) => {
    setViewingScreenshot(ss);
    setViewingImageUrl(null);
    setViewingImageLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/activities/screenshots/${ss.screenshot_id}/image`,
        { withCredentials: true, responseType: 'blob' }
      );
      setViewingImageUrl(URL.createObjectURL(res.data));
    } catch {
      /* placeholder */
    } finally {
      setViewingImageLoading(false);
    }
  };

  const closeViewer = () => {
    if (viewingImageUrl) URL.revokeObjectURL(viewingImageUrl);
    setViewingImageUrl(null);
    setViewingScreenshot(null);
    setViewingImageLoading(false);
  };

  const getCurrentIndex = () =>
    viewingScreenshot
      ? screenshots.findIndex((s) => s.screenshot_id === viewingScreenshot.screenshot_id)
      : -1;

  const navigateViewer = async (dir) => {
    const idx = getCurrentIndex();
    if (idx === -1) return;
    const newIdx =
      dir === 'prev'
        ? idx > 0
          ? idx - 1
          : screenshots.length - 1
        : idx < screenshots.length - 1
          ? idx + 1
          : 0;
    if (viewingImageUrl) URL.revokeObjectURL(viewingImageUrl);
    await openViewer(screenshots[newIdx]);
  };

  useEffect(() => {
    if (!viewingScreenshot) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') navigateViewer('prev');
      if (e.key === 'ArrowRight') navigateViewer('next');
      if (e.key === 'Escape') closeViewer();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewingScreenshot, screenshots, viewingImageUrl]);

  const staff = summary?.staff || [];
  const totalScreenshots = staff.reduce((a, s) => a + s.total, 0);
  const highIdleCount = staff.filter((s) => s.idle > s.active).length;

  const filteredStaff = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter(
      (s) =>
        s.name?.toLowerCase().includes(q) ||
        s.department?.toLowerCase().includes(q)
    );
  }, [staff, query]);

  const kpi = [
    {
      label: 'Screenshots',
      value: totalScreenshots,
      icon: Camera,
      accent: 'from-sky-500 to-pink-500',
      soft: 'bg-sky-50 text-sky-700 dark:bg-sky-950/50 dark:text-sky-300',
    },
    {
      label: 'Staff tracked',
      value: staff.length,
      icon: Users,
      accent: 'from-indigo-500 to-purple-500',
      soft: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300',
    },
    {
      label: 'High idle',
      value: highIdleCount,
      icon: EyeOff,
      accent: 'from-amber-500 to-orange-500',
      soft: 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300',
    },
  ];

  // ======= DETAIL VIEW =======
  if (detailStaff) {
    const stats = detailData?.stats || {};
    const idleHigh = (stats.idle_count ?? 0) > (stats.active_count ?? 0);

    return (
      <div className="relative pb-8" data-testid="screenshots-detail-page">
        <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

        <div className="relative space-y-5 md:space-y-6">
          <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur dark:border-slate-800 dark:bg-[hsl(222_47%_9%/0.92)]">
            <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div className="flex items-start gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setDetailStaff(null);
                      setSelectedScreenshots([]);
                    }}
                    className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
                    data-testid="back-to-summary"
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </button>
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h1 className="truncate text-2xl font-bold tracking-tight sm:text-3xl">
                        {detailStaff.name}
                      </h1>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold backdrop-blur">
                        <Camera className="h-3.5 w-3.5" />
                        Screenshots
                      </span>
                    </div>
                    <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-indigo-50/90">
                      <span>{selectedDate}</span>
                      <span className="opacity-50">·</span>
                      <span className="inline-flex items-center gap-1">
                        <Globe className="h-3.5 w-3.5" />
                        {timezone}
                      </span>
                      {detailStaff.department && (
                        <>
                          <span className="opacity-50">·</span>
                          <span>{detailStaff.department}</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>

                {selectedScreenshots.length > 0 && (
                  <Button
                    variant="destructive"
                    onClick={() => setDeleteDialogOpen(true)}
                    className="h-11 rounded-2xl bg-rose-600 hover:bg-rose-700"
                    data-testid="delete-selected-btn"
                  >
                    <Trash2 className="mr-1.5 h-4 w-4" />
                    Delete {selectedScreenshots.length}
                  </Button>
                )}
              </div>
            </div>

            {!loadingDetail && detailData && (
              <div className="flex flex-wrap items-center gap-2 border-b border-slate-100 bg-slate-50/70 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/60 sm:px-6">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 ring-1 ring-sky-100 dark:bg-sky-950/50 dark:text-sky-300 dark:ring-sky-800/50">
                  <Camera className="h-3 w-3" />
                  {stats.total_screenshots ?? 0} total
                </span>
                <span className="inline-flex items-center gap-1.5 rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700 ring-1 ring-indigo-100 dark:bg-indigo-950/50 dark:text-indigo-300 dark:ring-indigo-800/50">
                  <Monitor className="h-3 w-3" />
                  {stats.active_count ?? 0} active
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${
                    idleHigh
                      ? 'bg-rose-50 text-rose-700 ring-rose-100 dark:bg-rose-950/50 dark:text-rose-300 dark:ring-rose-800/50'
                      : 'bg-amber-50 text-amber-800 ring-amber-100 dark:bg-amber-950/50 dark:text-amber-300 dark:ring-amber-800/50'
                  }`}
                >
                  <EyeOff className="h-3 w-3" />
                  {stats.idle_count ?? 0} idle
                </span>
              </div>
            )}
          </section>

          {loadingDetail && (
            <div className="flex flex-col items-center justify-center gap-3 py-24">
              <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
              <p className="text-sm text-slate-500">Loading gallery…</p>
            </div>
          )}

          {!loadingDetail && detailData && (
            <>
              {screenshots.length > 0 ? (
                <section className="att-fade-up overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm dark:border-slate-800 dark:bg-[hsl(222_47%_9%)]">
                  <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800">
                    <div>
                      <h2 className="text-base font-bold text-slate-900 dark:text-slate-50">
                        Capture gallery
                      </h2>
                      <p className="mt-0.5 text-xs text-slate-500">
                        {screenshots.length} on this page
                        {detailData.pagination?.total
                          ? ` · ${detailData.pagination.total} total`
                          : ''}
                      </p>
                    </div>
                    <label className="inline-flex cursor-pointer items-center gap-2 rounded-2xl bg-slate-50 px-3 py-2 text-sm font-medium text-slate-600 ring-1 ring-slate-200/80 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-700">
                      <Checkbox
                        checked={allSelected}
                        onCheckedChange={handleSelectAll}
                        data-testid="select-all-checkbox"
                      />
                      Select all
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
                    {screenshots.map((ss, i) => {
                      const thumb = thumbnailUrl(ss);
                      const isSelected = selectedScreenshots.includes(ss.screenshot_id);
                      const isIdle = ss.screen_status === 'idle';
                      return (
                        <article
                          key={ss.screenshot_id || i}
                          className={`group relative flex flex-col overflow-hidden rounded-2xl border bg-white shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:bg-slate-950/40 ${
                            isSelected
                              ? 'border-purple-400 ring-2 ring-purple-200/80 dark:border-purple-500 dark:ring-purple-800/60'
                              : 'border-slate-200/80 dark:border-slate-800'
                          }`}
                          data-testid={`screenshot-card-${ss.screenshot_id}`}
                        >
                          <div
                            className="absolute left-2.5 top-2.5 z-10"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectScreenshot(ss.screenshot_id, !isSelected);
                            }}
                          >
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={(checked) =>
                                handleSelectScreenshot(ss.screenshot_id, checked)
                              }
                              className="border-white/80 bg-white/95 shadow-md dark:border-slate-600 dark:bg-slate-900"
                              data-testid={`checkbox-${ss.screenshot_id}`}
                            />
                          </div>

                          <span
                            className={`absolute right-2.5 top-2.5 z-10 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide shadow-sm backdrop-blur ${
                              isIdle
                                ? 'bg-amber-500/90 text-white'
                                : 'bg-indigo-500/90 text-white'
                            }`}
                          >
                            {isIdle ? 'Idle' : 'Active'}
                          </span>

                          <button
                            type="button"
                            onClick={() => openViewer(ss)}
                            className="relative aspect-[4/3] overflow-hidden bg-slate-100 dark:bg-slate-900"
                          >
                            {thumb ? (
                              <img
                                src={thumb}
                                alt={`Screenshot ${i + 1}`}
                                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                loading="lazy"
                                onError={(e) => {
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={`absolute inset-0 items-center justify-center bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-900 dark:to-slate-800 ${
                                thumb ? 'hidden' : 'flex'
                              }`}
                            >
                              <Image className="h-8 w-8 text-slate-300 dark:text-slate-600" />
                            </div>
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
                            <span className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-white/95 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-700 opacity-0 shadow transition group-hover:opacity-100 dark:bg-slate-900/95 dark:text-slate-200">
                              View
                            </span>
                          </button>

                          <div className="flex items-center justify-between gap-2 border-t border-slate-100 px-2.5 py-2 dark:border-slate-800">
                            <p className="truncate text-[11px] font-semibold tabular-nums text-slate-600 dark:text-slate-400">
                              {formatUtcTime(ss.created_at || ss.timestamp)}
                            </p>
                            <button
                              type="button"
                              onClick={(e) => handleDeleteSingle(ss.screenshot_id, e)}
                              className="flex h-7 w-7 items-center justify-center rounded-lg text-slate-400 opacity-0 transition hover:bg-rose-50 hover:text-rose-600 group-hover:opacity-100 dark:hover:bg-rose-950/40 dark:hover:text-rose-400"
                              data-testid={`delete-btn-${ss.screenshot_id}`}
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </article>
                      );
                    })}
                  </div>

                  {detailData.pagination?.pages > 1 && (
                    <div className="flex items-center justify-center gap-3 border-t border-slate-100 px-5 py-4 dark:border-slate-800">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page <= 1}
                        className="h-9 rounded-xl"
                      >
                        <ChevronLeft className="mr-1 h-4 w-4" />
                        Prev
                      </Button>
                      <span className="rounded-full bg-slate-100 px-3 py-1.5 text-xs font-semibold tabular-nums text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                        Page {page} of {detailData.pagination.pages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => p + 1)}
                        disabled={page >= detailData.pagination.pages}
                        className="h-9 rounded-xl"
                      >
                        Next
                        <ChevronRight className="ml-1 h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </section>
              ) : (
                <div className="att-fade-up rounded-3xl border border-dashed border-slate-200 bg-white/80 px-6 py-20 text-center dark:border-slate-700 dark:bg-[hsl(222_47%_9%)]">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 dark:bg-slate-800">
                    <Camera className="h-7 w-7 text-slate-400" />
                  </div>
                  <p className="mt-4 text-lg font-bold text-slate-900 dark:text-slate-50">
                    No screenshots for this date
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    Captures appear when the desktop agent is active.
                  </p>
                </div>
              )}
            </>
          )}
        </div>

        <Dialog
          open={!!viewingScreenshot}
          onOpenChange={(open) => {
            if (!open) closeViewer();
          }}
        >
          <DialogContent className="max-w-5xl gap-0 overflow-hidden rounded-3xl border-slate-200 p-0 dark:border-slate-800 dark:bg-[hsl(222_47%_9%)]">
            <DialogHeader className="border-b border-slate-100 px-5 py-4 dark:border-slate-800">
              <DialogTitle className="flex items-center justify-between gap-3 pr-8">
                <span className="flex items-center gap-2 text-base font-bold text-slate-900 dark:text-slate-50">
                  <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
                    <Camera className="h-4 w-4" />
                  </span>
                  Screenshot
                  {viewingScreenshot && (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                        viewingScreenshot.screen_status === 'idle'
                          ? 'bg-amber-100 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300'
                          : 'bg-indigo-100 text-indigo-800 dark:bg-indigo-950/50 dark:text-indigo-300'
                      }`}
                    >
                      {viewingScreenshot.screen_status === 'idle' ? 'Idle' : 'Active'}
                    </span>
                  )}
                </span>
                <span className="text-sm font-normal tabular-nums text-slate-500">
                  {getCurrentIndex() + 1} / {screenshots.length}
                  {viewingScreenshot && (
                    <span className="ml-2 text-slate-400">
                      {formatUtcTime(viewingScreenshot.created_at || viewingScreenshot.timestamp)}
                    </span>
                  )}
                </span>
              </DialogTitle>
            </DialogHeader>
            <div className="relative bg-slate-950">
              <button
                type="button"
                onClick={() => navigateViewer('prev')}
                className="absolute left-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl bg-white/10 text-white backdrop-blur hover:bg-white/20"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => navigateViewer('next')}
                className="absolute right-3 top-1/2 z-10 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-2xl bg-white/10 text-white backdrop-blur hover:bg-white/20"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
              <div className="flex min-h-[420px] items-center justify-center p-4">
                {viewingImageLoading ? (
                  <Loader2 className="h-10 w-10 animate-spin text-indigo-400" />
                ) : viewingImageUrl ? (
                  <img
                    src={viewingImageUrl}
                    alt="Screenshot"
                    className="max-h-[72vh] w-full object-contain"
                  />
                ) : (
                  <Image className="h-16 w-16 text-slate-600" />
                )}
              </div>
            </div>
            <p className="bg-slate-50 px-5 py-2.5 text-center text-xs text-slate-400 dark:bg-slate-900/80">
              ← → to navigate · Esc to close
            </p>
          </DialogContent>
        </Dialog>

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="rounded-3xl">
            <AlertDialogHeader>
              <AlertDialogTitle>Delete screenshots</AlertDialogTitle>
              <AlertDialogDescription>
                Delete {selectedScreenshots.length} selected screenshot(s)? This cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={deleting} className="rounded-xl">
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteSelected}
                disabled={deleting}
                className="rounded-xl bg-rose-600 hover:bg-rose-700"
              >
                {deleting ? 'Deleting…' : 'Delete'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    );
  }

  // ======= SUMMARY VIEW =======
  return (
    <div className="relative pb-8" data-testid="screenshots-report-page">
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-56 bg-[radial-gradient(ellipse_at_top,_rgba(99,102,241,0.12),_transparent_60%)]" />

      <div className="relative space-y-5 md:space-y-6">
        <section className="att-fade-up overflow-hidden rounded-3xl border border-white/70 bg-white/90 shadow-[0_20px_50px_-28px_rgba(15,23,42,0.35)] backdrop-blur dark:border-slate-800 dark:bg-[hsl(222_47%_9%/0.92)]">
          <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 px-4 py-4 text-white sm:px-6 sm:py-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex items-start gap-3">
                <button
                  type="button"
                  onClick={() => navigate('/admin/reports')}
                  className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-white ring-1 ring-white/25 hover:bg-white/25"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <div>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">Screenshots</h1>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/20 px-2.5 py-1 text-xs font-semibold backdrop-blur">
                      <LayoutGrid className="h-3.5 w-3.5" />
                      Activity report
                    </span>
                  </div>
                  <p className="mt-1.5 flex items-center gap-1.5 text-sm text-indigo-50/90">
                    <Globe className="h-3.5 w-3.5" />
                    Captures in {timezone}
                  </p>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <DateNavInput
                  value={selectedDate}
                  onChange={setSelectedDate}
                  className="!rounded-2xl !border-white/25 !bg-white/15 overflow-hidden [&_button]:!border-white/20 [&_button]:!text-white/80 [&_button:hover]:!bg-white/10 [&_input]:!bg-transparent [&_input]:!text-white [&_input]:!w-36"
                />
              </div>
            </div>
          </div>
        </section>

        {loadingSummary && (
          <div className="flex flex-col items-center justify-center gap-3 py-24">
            <div className="h-12 w-12 animate-spin rounded-full border-2 border-indigo-200 border-t-indigo-600" />
            <p className="text-sm text-slate-500">Loading summary…</p>
          </div>
        )}

        {!loadingSummary && summary && (
          <>
            <section
              className="att-fade-up grid grid-cols-1 gap-3 sm:grid-cols-3 md:gap-4"
              style={{ animationDelay: '60ms' }}
            >
              {kpi.map((k) => (
                <div
                  key={k.label}
                  className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm transition duration-300 hover:-translate-y-0.5 hover:shadow-lg dark:border-slate-800 dark:bg-[hsl(222_47%_9%)]"
                >
                  <div
                    className={`absolute -right-6 -top-6 h-24 w-24 rounded-full bg-gradient-to-br ${k.accent} opacity-10 blur-xl transition group-hover:opacity-20`}
                  />
                  <div className="relative flex items-center gap-3">
                    <div
                      className={`flex h-11 w-11 items-center justify-center rounded-2xl ${k.soft}`}
                    >
                      <k.icon className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                        {k.label}
                      </p>
                      <p className="mt-0.5 text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-50">
                        {k.value}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </section>

            <section
              className="att-fade-up overflow-hidden rounded-3xl border border-slate-200/70 bg-white shadow-sm dark:border-slate-800 dark:bg-[hsl(222_47%_9%)]"
              style={{ animationDelay: '100ms' }}
            >
              <div className="flex flex-col gap-3 border-b border-slate-100 px-5 py-4 dark:border-slate-800 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <h2 className="text-base font-bold text-slate-900 dark:text-slate-50">
                    Staff captures
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {selectedDate} · click a card to open the gallery
                  </p>
                </div>
                <div className="relative w-full sm:max-w-xs">
                  <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <Input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Search staff…"
                    className="h-10 rounded-2xl border-slate-200 bg-slate-50/80 pl-10 dark:border-slate-700 dark:bg-slate-900/60"
                    data-testid="screenshots-staff-search"
                  />
                </div>
              </div>

              {filteredStaff.length === 0 ? (
                <div className="px-6 py-20 text-center">
                  <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-3xl bg-slate-100 dark:bg-slate-800">
                    <Camera className="h-7 w-7 text-slate-400" />
                  </div>
                  <p className="mt-4 text-lg font-bold text-slate-900 dark:text-slate-50">
                    {query ? `No match for “${query}”` : 'No screenshots on this date'}
                  </p>
                  <p className="mt-1 text-sm text-slate-500">
                    {query
                      ? 'Try another name or clear the search.'
                      : 'Screenshots appear when the desktop agent is active.'}
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
                  {filteredStaff.map((s, idx) => {
                    const highIdle = s.idle > s.active;
                    const activePct =
                      s.total > 0 ? Math.round((s.active / s.total) * 100) : 0;
                    return (
                      <button
                        key={s.staff_id}
                        type="button"
                        onClick={() => openDetail(s)}
                        className="group relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white p-4 text-left shadow-sm transition duration-300 hover:-translate-y-0.5 hover:border-purple-200 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 dark:border-slate-800 dark:bg-slate-950/40 dark:hover:border-purple-800"
                        data-testid={`staff-row-${s.staff_id}`}
                        style={{ animationDelay: `${Math.min(idx, 8) * 30}ms` }}
                      >
                        <div className="flex items-start gap-3">
                          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-pink-600 text-base font-bold text-white shadow-md shadow-sky-500/20">
                            {s.name?.[0]?.toUpperCase() || '?'}
                            {highIdle && (
                              <span className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-rose-500 text-[8px] text-white ring-2 ring-white dark:ring-slate-950">
                                !
                              </span>
                            )}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0">
                                <p className="truncate font-semibold text-slate-900 dark:text-slate-50">
                                  {s.name}
                                </p>
                                <p className="mt-0.5 truncate text-xs text-slate-500">
                                  {s.department || 'No department'}
                                </p>
                              </div>
                              <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-slate-300 transition group-hover:translate-x-0.5 group-hover:text-purple-600 dark:text-slate-600" />
                            </div>

                            <div className="mt-3 flex flex-wrap items-center gap-1.5">
                              <span className="inline-flex items-center gap-1 rounded-lg bg-sky-50 px-2 py-1 text-[11px] font-bold text-sky-700 dark:bg-sky-950/50 dark:text-sky-300">
                                <Camera className="h-3 w-3" />
                                {s.total}
                              </span>
                              <span className="inline-flex items-center gap-1 rounded-lg bg-indigo-50 px-2 py-1 text-[11px] font-bold text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300">
                                <Monitor className="h-3 w-3" />
                                {s.active}
                              </span>
                              <span
                                className={`inline-flex items-center gap-1 rounded-lg px-2 py-1 text-[11px] font-bold ${
                                  highIdle
                                    ? 'bg-rose-50 text-rose-700 dark:bg-rose-950/50 dark:text-rose-300'
                                    : 'bg-amber-50 text-amber-800 dark:bg-amber-950/50 dark:text-amber-300'
                                }`}
                              >
                                <EyeOff className="h-3 w-3" />
                                {s.idle}
                                {highIdle && (
                                  <span className="ml-0.5 rounded bg-rose-500/15 px-1 text-[9px] uppercase">
                                    High
                                  </span>
                                )}
                              </span>
                            </div>

                            <div className="mt-3">
                              <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                                <span className="inline-flex items-center gap-1">
                                  <Sparkles className="h-3 w-3" />
                                  Active share
                                </span>
                                <span className="tabular-nums">{activePct}%</span>
                              </div>
                              <div className="h-1.5 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                <div
                                  className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-purple-500 transition-all"
                                  style={{ width: `${activePct}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
}
